import styled, { keyframes } from 'styled-components';
import { 
  Card, 
  Button, 
  StatusBadge,
  Tag 
} from './index';

// Space-themed animation keyframes
// eslint-disable-next-line no-unused-vars
const glowPulse = keyframes`
  0% { box-shadow: 0 0 10px rgba(99, 102, 241, 0.3), inset 0 0 5px rgba(99, 102, 241, 0.1); }
  50% { box-shadow: 0 0 15px rgba(99, 102, 241, 0.5), inset 0 0 8px rgba(99, 102, 241, 0.2); }
  100% { box-shadow: 0 0 10px rgba(99, 102, 241, 0.3), inset 0 0 5px rgba(99, 102, 241, 0.1); }
`;

// eslint-disable-next-line no-unused-vars
const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

// Report card components
export const ReportCard = styled(Card)`
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  cursor: pointer;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(99, 102, 241, 0.2);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  position: relative;
  
  &:hover {
    transform: translateY(-5px);
    border-color: rgba(99, 102, 241, 0.5);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
    
    &:after {
      opacity: 1;
    }
  }
  
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    background: linear-gradient(
      120deg,
      transparent,
      transparent,
      rgba(99, 102, 241, 0.03),
      transparent,
      transparent
    );
    opacity: 0;
    transition: all 0.3s ease;
  }
`;

export const ReportInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ReportTitle = styled.h3`
  margin: 0 0 0.3rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: white;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const StudentDetails = styled.p`
  margin: 0.2rem 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  transition: color 0.3s ease;
  
  ${ReportCard}:hover & {
    color: rgba(255, 255, 255, 0.9);
  }
`;

export const StudentAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(79, 70, 229, 0.2) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 2px solid rgba(99, 102, 241, 0.3);
  position: relative;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: all 0.3s ease;
  }
  
  ${ReportCard}:hover & {
    border-color: rgba(99, 102, 241, 0.6);
    transform: scale(1.05);
    box-shadow: 0 0 15px rgba(99, 102, 241, 0.2);
    
    img {
      transform: scale(1.1);
    }
  }
`;

export const CompanyLogo = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid rgba(99, 102, 241, 0.3);
  transition: all 0.3s ease;
  margin-right: 0.5rem;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  ${ReportCard}:hover & {
    border-color: rgba(99, 102, 241, 0.5);
  }
`;

// Modal components
export const ReportModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
`;

export const ModalCard = styled(Card)`
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  background: rgba(15, 23, 42, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(99, 102, 241, 0.3);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
  
  &::-webkit-scrollbar {
    width: 8px;
    background: rgba(15, 23, 42, 0.3);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.5);
    border-radius: 8px;
  }
`;

export const CloseButton = styled.button`
  background: rgba(99, 102, 241, 0.2);
  color: white;
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 8px;
  padding: 0.5rem 0.8rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(99, 102, 241, 0.3);
    box-shadow: 0 0 15px rgba(99, 102, 241, 0.4);
    transform: translateY(-2px);
  }
`;

// Action buttons
export const ActionButton = styled(Button)`
  background: rgba(79, 70, 229, 0.7);
  color: white;
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 8px;
  padding: 0.6rem 1rem;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  
  &:hover {
    background: rgba(79, 70, 229, 0.9);
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

// Status button group
export const StatusButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
`;

export const StatusButton = styled(Button)`
  flex: 1;
  min-width: 120px;
  background: ${props => 
    props.variant === 'accent' ? 'rgba(16, 185, 129, 0.2)' :
    props.variant === 'warning' ? 'rgba(245, 158, 11, 0.2)' :
    props.variant === 'danger' ? 'rgba(239, 68, 68, 0.2)' :
    'rgba(15, 23, 42, 0.6)'
  };
  color: ${props => 
    props.variant === 'accent' ? 'rgba(16, 185, 129, 0.9)' :
    props.variant === 'warning' ? 'rgba(245, 158, 11, 0.9)' :
    props.variant === 'danger' ? 'rgba(239, 68, 68, 0.9)' :
    'rgba(255, 255, 255, 0.9)'
  };
  border: 1px solid ${props => 
    props.variant === 'accent' ? 'rgba(16, 185, 129, 0.3)' :
    props.variant === 'warning' ? 'rgba(245, 158, 11, 0.3)' :
    props.variant === 'danger' ? 'rgba(239, 68, 68, 0.3)' :
    'rgba(99, 102, 241, 0.3)'
  };
  padding: 0.6rem 1rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => 
      props.variant === 'accent' ? 'rgba(16, 185, 129, 0.3)' :
      props.variant === 'warning' ? 'rgba(245, 158, 11, 0.3)' :
      props.variant === 'danger' ? 'rgba(239, 68, 68, 0.3)' :
      'rgba(99, 102, 241, 0.3)'
    };
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

// Comment section
export const CommentSection = styled.div`
  margin-top: 2rem;
  background: rgba(15, 23, 42, 0.3);
  padding: 1.2rem;
  border-radius: 10px;
  border: 1px solid rgba(99, 102, 241, 0.15);
`;

export const CommentTextarea = styled.textarea`
  width: 100%;
  border-radius: 8px;
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.3);
  padding: 1rem;
  color: white;
  resize: vertical;
  font-size: 0.9rem;
  min-height: 100px;
  margin-bottom: 1rem;
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
  }
`;

// Enhanced badges
export const SpaceStatusBadge = styled(StatusBadge)`
  padding: 0.35rem 0.8rem;
  border-radius: 6px;
  font-weight: 600;
  text-shadow: 0 0 5px rgba(0, 0, 0, 0.2);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
`;

export const SpaceTag = styled(Tag)`
  background: rgba(99, 102, 241, 0.15);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 6px;
  padding: 0.3rem 0.6rem;
  font-size: 0.8rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(99, 102, 241, 0.25);
    transform: translateY(-2px);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
  }
`;
